import { useState, useRef, useEffect, useCallback } from "react";
import { useTaskChat } from "hooks/useTaskChat";
import { useAssistantIdentity } from "hooks/useAssistantIdentity";
import Message from "types/Message";
import React from "react";
import { connectToWebSocket, toCamelCase, uploadFile } from "apiClient";
import { Loader2, Paperclip, Send, X } from "lucide-react";
import { MessageBalloon } from "./MessageBalloon";
import { useAuth } from "context/AuthContext";
import {
  ensureMessageListKey,
  isOptimisticMessage,
  upsertWebsocketMessage,
} from "utils/message";

interface TaskChatProps {
  taskId: number;
}

export function TaskChat({ taskId }: TaskChatProps) {
  const [page, setPage] = useState(1);
  const [allMessages, setAllMessages] = useState<Message[]>([]);
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const tr = (es: string, en: string) => (locale === "es" ? es : en);
  const { assistantName } = useAssistantIdentity();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { messages, loading, error, next, sendMessage } = useTaskChat(
    taskId,
    page
  );

  const loadedPages = useRef<Set<number>>(new Set());
  const socketRef = useRef<ReturnType<typeof connectToWebSocket> | null>(null);

  useEffect(() => {
    // Close any existing socket before creating a new one
    if (socketRef.current) {
      socketRef.current.close();
    }

    const socket = connectToWebSocket(`/ws/tasks/${taskId}/chat/`, (event) => {
      const message = ensureMessageListKey(
        toCamelCase(JSON.parse(event.data)) as Message
      );
      setAllMessages((prev) => {
        // Remove optimistic messages with same content
        const filtered = prev.filter(
          (m) => !(isOptimisticMessage(m) && m.content === message.content)
        );

        return upsertWebsocketMessage(filtered, message);
      });
    });

    socketRef.current = socket;

    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, [taskId]);

  useEffect(() => {
    setAllMessages([]);
    setPage(1);
    loadedPages.current = new Set();
  }, [taskId]);

  useEffect(() => {
    if (messages && messages.length > 0 && !loadedPages.current.has(page)) {
      setAllMessages((prev) => {
        const existingKeys = new Set(prev.map((m) => m.listKey));
        const newMessages = messages.filter((m) => !existingKeys.has(m.listKey));

        if (newMessages.length > 0) {
          loadedPages.current.add(page);
        }

        return [...prev, ...newMessages];
      });
    }
  }, [messages, page]);

  // Scroll to bottom on first load only
  useEffect(() => {
    if (page === 1 && allMessages.length > 0) {
      bottomRef.current?.scrollIntoView({ behavior: "auto" });
    }
  }, [page, allMessages]);

  // Scroll listener to load more messages when reaching top
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || loading || !next) return;

    // still fetching
    if (!loadedPages.current.has(page)) return;

    if (container.scrollTop < 50) {
      setPage((prev) => prev + 1);
    }
  }, [loading, next, page]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  const [messageToSend, setMessageToSend] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setFileError(
          tr(
            "El archivo es demasiado grande (máx 50MB)",
            "File is too large (max 50MB)"
          )
        );
        setSelectedFile(null);
      } else {
        setFileError(null);
        setSelectedFile(file);
      }
    }
    // Reset input value so same file can be selected again if removed
    e.target.value = "";
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError(null);
  };

  const handleSend = async () => {
    if (uploading) return;

    const content = messageToSend.trim();
    if (!content && !selectedFile) return;

    let fileUrl: string | null = null;
    if (selectedFile) {
      setUploading(true);
      try {
        fileUrl = await uploadFile(selectedFile);
      } catch (err) {
        setFileError(tr("Error subiendo el archivo", "Error uploading file"));
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    const optimisticId = -Date.now();
    const optimisticMessage: Message = {
      id: optimisticId,
      listKey: String(optimisticId),
      type: "manager",
      content,
      fileUrl,
      fileName: selectedFile ? selectedFile.name : null,
      sentAt: new Date().toISOString(),
      channel: "fake",
      subject: null,
    };

    setAllMessages((prev) => [optimisticMessage, ...prev]);
    setMessageToSend("");
    setSelectedFile(null);
    setFileError(null);
    await sendMessage(content, fileUrl);
  };

  const canSendMessage =
    !uploading &&
    (Boolean(messageToSend.trim()) || Boolean(selectedFile));

  return (
    <div className="chat-container flex h-full min-h-0 w-full min-w-0 flex-1 flex-col gap-2 lg:gap-3">
      {/* Messages list */}
      <div
        ref={scrollContainerRef}
        className="chat-messages-pane min-h-0 flex-1 overflow-y-auto space-y-2 px-2 py-2 lg:space-y-4 lg:px-3 lg:py-3 w-full"
      >
        <div ref={topRef} />
        {error && (
          <div className="text-red-500 text-xs lg:text-sm">{error}</div>
        )}
        {[...allMessages].reverse().map((msg) => (
          <MessageBalloon key={msg.listKey} message={msg} contactId={undefined} />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Message input */}
      <div className="flex w-full shrink-0 items-center gap-1 lg:gap-2">
        <textarea
          value={messageToSend}
          onChange={(e) => setMessageToSend(e.target.value)}
          placeholder={tr(
            `Enviar instrucción a ${assistantName}...`,
            `Send instruction to ${assistantName}...`
          )}
          className="flex-1 border border-gray-300 rounded p-2 text-base lg:text-sm resize-none h-10 lg:h-12"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (e.metaKey || e.shiftKey) {
                return;
              }
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={uploading}
        />
        <label className="cursor-pointer flex items-center p-1 lg:p-2">
          <input
            type="file"
            className="hidden"
            onChange={handleFileChange}
            disabled={uploading}
          />
          <Paperclip className="h-4 w-4 lg:h-5 lg:w-5 text-gray-500" />
        </label>
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSendMessage}
          className={`flex shrink-0 items-center justify-center rounded-full p-1 transition-all ${
            canSendMessage || uploading
              ? "text-primary hover:scale-105 hover:text-primary-light active:scale-95"
              : "cursor-not-allowed text-stone-300"
          }`}
          aria-label={
            uploading
              ? tr("Enviando mensaje", "Sending message")
              : tr("Enviar mensaje", "Send message")
          }
          title={uploading ? tr("Enviando...", "Sending...") : tr("Enviar", "Send")}
        >
          {uploading ? (
            <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
          ) : (
            <Send
              className={`h-8 w-8 ${canSendMessage ? "fill-primary/25" : ""}`}
              strokeWidth={canSendMessage ? 2.5 : 2}
              aria-hidden
            />
          )}
        </button>
      </div>
      {selectedFile && (
        <div className="flex items-center mt-1 bg-gray-100 rounded px-2 py-1 text-xs">
          <span className="truncate max-w-xs">{selectedFile.name}</span>
          <button onClick={handleRemoveFile} className="ml-2">
            <X className="h-3 w-3 lg:h-4 lg:w-4 text-gray-400" />
          </button>
        </div>
      )}
      {fileError && (
        <div className="text-xs text-red-500 mt-1">{fileError}</div>
      )}
    </div>
  );
}
